import { mapValues } from 'lodash'
import { Dict, memoizedGet as get } from '@chakra-ui/utils'
import { WithCSSVar, useBreakpointValue, useMediaQuery } from '@chakra-ui/react'
import {
  HubResponsiveArray,
  useToken,
  DEFAULT_BREAKPOINT,
  HubResponsiveValue,
} from '../'

// TODO: what on earth is the actual type here
export const mapRules = (
  obj: Record<string, any>,
  mapper: (value: any, obj: Record<string, any>, path: string[]) => any,
  path: string[] = []
): Record<string, any> =>
  mapValues(obj, (value, key) => {
    if (value == null) {
      return value
    }
    // For media queries we recurse
    if (typeof value === 'object') {
      return mapRules(value, mapper, [...path, key])
    }
    return mapper(value, obj, [...path, key])
  })

export function useCssValueFromResponsiveProp<T extends string | number>(
  prop: HubResponsiveArray<T>,
  themeKey: string,
  defaultBreakpoint: string = DEFAULT_BREAKPOINT
): string {
  const propArray = Array.isArray(prop) ? prop : [prop]
  const propForBreakpoint: T | undefined = useBreakpointValue<T>(
    propArray as T[],
    defaultBreakpoint
  )

  const [cssValue] = useToken(
    themeKey,
    propForBreakpoint ? [propForBreakpoint] : []
  )

  return cssValue
}

// Necessary until this is implemented in ChakraUI.
// More details: https://github.com/chakra-ui/chakra-ui/issues/4055
export function getToken<T extends string | number, U extends object = Dict>(
  theme: WithCSSVar<U>,
  scale: string,
  token: T | T[],
  fallback?: T | T[]
) {
  if (Array.isArray(token)) {
    let fallbackArr: T[] = []
    if (fallback) {
      fallbackArr = Array.isArray(fallback) ? fallback : [fallback]
    }

    return token.map((token, index) => {
      const path = `${scale}.${token}`
      return get(theme, path, fallbackArr[index] ?? token)
    })
  }

  const path = `${scale}.${token}`
  return get(theme, path, fallback ?? token)
}

export { useBreakpointValue, useMediaQuery }

type Scalar = number | string | boolean

/*
If you know how to calculate a value for a Hub property that's
some type (a string, number or boolean) based on some other Hub
property that's some other type (ditto), you can use mapResponsiveValue
to generalise that calculation to a HubResponsiveValue and get a
HubResponsiveValue back.
*/

export function mapResponsiveValue<From extends Scalar, To extends Scalar>(
  responsiveValue: HubResponsiveValue<From> | undefined,
  scalarTransform: (from: From | undefined | null) => To | undefined | null
): HubResponsiveValue<To> | undefined {
  if (Array.isArray(responsiveValue)) {
    return responsiveValue.map(value => scalarTransform(value) ?? null)
  } else if (typeof responsiveValue === 'object') {
    return mapValues(
      responsiveValue,
      value => scalarTransform(value) ?? undefined
    )
  } else {
    return scalarTransform(responsiveValue) ?? undefined
  }
}
